import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { environment } from "../../../../../environments/environment";
import { Observable, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class DownloadService {
  private http = inject(HttpClient);

  /**
   * TODO: refactor this to use the new api.
   * Download a file.
   * @param fileInfo
   * @param saveFile
   */
  downloadFile(key: string, saveFile = true) {
    return this.http
      .get(`${environment.url}/resource/download/${key}`, {
        observe: "events",
        reportProgress: true,
        responseType: "blob",
      })
      .pipe(
        tap(events => {
          // if the file is downloaded and save file option is true, then save the file to the user device.
          if (events instanceof HttpResponse && saveFile) {
            this.saveFile(events.body as any, key);
          }
        })
      );
  }

  packageAndDownloadFiles(files: string[], saveFile = true) {
    return this.http
      .post(
        `${environment.url}/resource/download-multiple`,
        {
          files,
        },
        {
          observe: "events",
          reportProgress: true,
          responseType: "blob",
        }
      )
      .pipe(
        tap(events => {
          // if the file is downloaded and save file option is true, then save the file to the user device.
          if (events instanceof HttpResponse && saveFile) {
            this.saveFile(events.body as any, "download.zip");
          }
        })
      );
  }

  /**
   * Saves the file to the user device.
   * @param file
   */
  saveFile(blob: any, key: string) {
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    // TODO get the file name using the headers.
    anchor.download = key.split("/")[key.split("/").length - 1].split("_").slice(1).join("_") || key;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  /**
   * Returns the actual file security.
   * @param fileKey
   */
  getSecurityLevel(key: string) {
    if (key.substring(0, 7) === "normal/") {
      return "normal";
    }
    if (/^[a-zA-Z\d]{32}$/.test(key)) {
      return "temp";
    }
    if (/^[a-zA-Z\d]{36}$/.test(key)) {
      return "secure";
    }
    return "INVALID";
  }

  /**
   * converts an arrayBuffer into Base64
   * @param arrayBuffer
   */
  arrayBufferToBase64(arrayBuffer: ArrayBuffer): Observable<string> {
    return new Observable(subscriber => {
      const blob = new Blob([new Uint8Array(arrayBuffer)], { type: this.determineFileType(arrayBuffer) });
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        subscriber.next(reader.result as string);
      };
    });
  }

  /**
   * it checks the array buffer header to determine its type.
   * @param buffer
   */
  determineFileType(buffer: ArrayBuffer): string {
    const header = new Uint8Array(buffer, 0, 4);
    const type = new Map<number[], string>([
      [[0x25, 0x50, 0x44, 0x46], "application/pdf"], // PDF
      [[0x47, 0x49, 0x46, 0x38], "image/gif"], // GIF
      [[0x89, 0x50, 0x4e, 0x47], "image/png"], // PNG
      [[0xff, 0xd8, 0xff], "image/jpeg"], // JPEG
      [[0x52, 0x49, 0x46, 0x46], "audio/wav"], // WAV
      [[0x00, 0x00, 0x01, 0xba], "video/mpeg"], // MPEG
      [[0x00, 0x00, 0x01, 0xb3], "video/mpeg"], // MPEG
      [[0x00, 0x00, 0x01, 0xb0], "video/mpeg"], // MPEG
      [[0x00, 0x00, 0x01, 0x67], "video/3gpp"], // 3GPP
      [[0x00, 0x00, 0x00, 0x1c], "video/3gpp"], // 3GPP
      [[0x00, 0x00, 0x00, 0x20], "video/mp4"], // MP4
      [[0x00, 0x00, 0x00, 0x18], "video/mp4"], // MP4
      [[0x66, 0x74, 0x79, 0x70], "video/quicktime"], // MOV
      [[0x4f, 0x67, 0x67, 0x53], "application/ogg"], // OGG
    ]);
    for (const [signature, mediaType] of type) {
      if (header.length >= signature.length && signature.every((x, i) => header[i] === x)) {
        return mediaType;
      }
    }
    return "unknown";
  }
}
